/**
 * SecureFileSignpost — s111 amendment.
 *
 * Replaces the full inline SecureFileOnboardingStep create flow when a planner focuses a
 * Cat A field OUTSIDE the Protect Your Access page and hasn't set up their secure file yet.
 * Instead of creating the file in place, it explains why the field is locked and sends them
 * to the Protect Your Access page — the single home for secure-file setup.
 *
 * Deliberately lightweight: a dialog with copy + a navigate button. No location input,
 * passphrase, strength meter, or save logic (that all lives in SecureFileOnboardingStep,
 * still used by Protect Your Access's own Create CTA and Settings → Security).
 *
 * The field unlocks normally on return once `secure_file_setup_at` is set — this component
 * only redirects; it does not create the file. Copy is placeholder (Reade to polish).
 */

import { useLocation } from "wouter";
import { ShieldCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CATEGORY_SLUGS } from "@shared/constants/categories";

export interface SecureFileSignpostProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The triggering field's display name, woven into the copy. */
  fieldName?: string;
}

export function SecureFileSignpost({ open, onOpenChange, fieldName }: SecureFileSignpostProps) {
  const [, navigate] = useLocation();

  const goToSetup = () => {
    onOpenChange(false);
    navigate(`/category/${CATEGORY_SLUGS.PROTECT_YOUR_ACCESS}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[#FEFCF8] border border-[#D9D1C1]" data-testid="secure-file-signpost">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[#4F6B51]" />
            <DialogTitle className="font-['Fraunces'] font-medium text-[#2B2A26]">
              Set up your Secure File first
            </DialogTitle>
          </div>
        </DialogHeader>
        <DialogDescription asChild>
          <div className="space-y-3 text-sm text-[#2B2A26]">
            {/* PLACEHOLDER COPY — Reade to polish. */}
            <p>
              {fieldName ? <>To enter your <strong>{fieldName}</strong>, you'll</> : "You'll"} set up your
              Secure File on the <strong>Protect Your Access</strong> page. It stays on your own
              computer — never on our servers.
            </p>
            <p>Once it's ready, come back here and enter this.</p>
          </div>
        </DialogDescription>
        <div className="flex gap-3 pt-2">
          <Button
            size="sm"
            onClick={goToSetup}
            className="bg-[#4F6B51] text-white hover:bg-[#425A44]"
            data-testid="signpost-go-to-setup"
          >
            Go to Protect Your Access
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} data-testid="signpost-dismiss">
            Not now
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
